import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { crewService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import Modal from "./Modal";
import type { CrewMember } from "../types/database";

interface CrewAssignmentProps {
  selectedCrew: string[];
  onCrewChange: (crew: string[]) => void;
  jobId: string;
}

export default function CrewAssignment({
  selectedCrew,
  onCrewChange,
  jobId,
}: CrewAssignmentProps) {
  const { currentOrganization } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Fetch all crew members
  const {
    data: crewMembers = [],
    isLoading,
    error,
  } = useQuery<CrewMember[]>({
    queryKey: ["crew", currentOrganization?.id],
    queryFn: () => crewService.getCrewMembers(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Get all available tags from crew members
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    crewMembers.forEach((member) => {
      member.tags?.forEach((tag) => tags.add(tag));
      member.specialties?.forEach((specialty) => tags.add(specialty));
    });
    return Array.from(tags).sort();
  }, [crewMembers]);

  // Filter crew members based on search and tags
  const filteredCrewMembers = useMemo(() => {
    return crewMembers.filter((member) => {
      // Search by name
      const nameMatch = searchTerm
        ? `${member.first_name} ${member.last_name}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : true;

      // Filter by tags
      const tagMatch =
        selectedTags.length === 0 ||
        selectedTags.some((tag) =>
          [...(member.tags || []), ...(member.specialties || [])].includes(tag)
        );

      return nameMatch && tagMatch;
    });
  }, [crewMembers, searchTerm, selectedTags]);

  // Get selected crew member details
  const selectedCrewDetails = useMemo(() => {
    return selectedCrew
      .map((crewId) => crewMembers.find((member) => member.id === crewId))
      .filter(Boolean) as CrewMember[];
  }, [selectedCrew, crewMembers]);

  // Mutation for updating crew member job history
  const updateCrewMemberMutation = useMutation({
    mutationFn: async (crewMemberId: string) => {
      const crewMember = crewMembers.find((cm) => cm.id === crewMemberId);
      if (!crewMember) return;

      // Add job to crew member's job history
      const jobHistory = crewMember.job_history || [];
      if (!jobHistory.includes(jobId)) {
        const newJobHistory = [...jobHistory, jobId];

        return crewService.updateCrewMember(
          crewMemberId,
          { job_history: newJobHistory },
          currentOrganization?.id || ""
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crew"] });
    },
  });

  // Mutation for updating crew member job history (removing job)
  const removeCrewMemberJobHistoryMutation = useMutation({
    mutationFn: async (crewMemberId: string) => {
      const crewMember = crewMembers.find((cm) => cm.id === crewMemberId);
      if (!crewMember) return;

      // Remove job from crew member's job history
      const jobHistory = crewMember.job_history || [];
      const newJobHistory = jobHistory.filter(
        (historyJobId) => historyJobId !== jobId
      );

      return crewService.updateCrewMember(
        crewMemberId,
        { job_history: newJobHistory },
        currentOrganization?.id || ""
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crew"] });
    },
  });

  // Add crew member to job
  const addCrewMember = (crewMemberId: string) => {
    if (!selectedCrew.includes(crewMemberId)) {
      const newCrew = [...selectedCrew, crewMemberId];
      onCrewChange(newCrew);

      // Update crew member's job history
      updateCrewMemberMutation.mutate(crewMemberId);
    }
  };

  // Remove crew member from job
  const removeCrewMember = (crewMemberId: string) => {
    const newCrew = selectedCrew.filter((id) => id !== crewMemberId);
    onCrewChange(newCrew);

    // Remove job from crew member's job history
    removeCrewMemberJobHistoryMutation.mutate(crewMemberId);
  };

  // Navigate to crew member details
  const navigateToCrewMember = (crewMemberId: string) => {
    navigate(`/org/${currentOrganization?.id}/crew/${crewMemberId}`);
  };

  // Toggle tag selection
  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Error loading crew members. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selected Crew Members */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Assigned Crew ({selectedCrewDetails.length})
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-1 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm"
          >
            Add Crew
          </button>
        </div>

        {selectedCrewDetails.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              No crew members assigned to this job yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedCrewDetails.map((member) => (
              <div
                key={member.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigateToCrewMember(member.id)}
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                    {member.profile_image ? (
                      <img
                        src={member.profile_image}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      `${member.first_name.charAt(0)}${member.last_name.charAt(
                        0
                      )}`
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {member.first_name} {member.last_name}
                    </p>
                    <p className="text-sm text-gray-500">{member.role_type}</p>
                    {member.specialties && member.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.specialties.slice(0, 2).map((specialty) => (
                          <span
                            key={specialty}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {specialty}
                          </span>
                        ))}
                        {member.specialties.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{member.specialties.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCrewMember(member.id);
                  }}
                  className="text-sm text-red-600 hover:text-red-900"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Crew Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Crew Members"
        size="lg"
      >
        <div className="space-y-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search crew members by name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Tag Filters */}
          {allTags.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Filter by skills/tags:
              </h4>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-2 py-1 text-xs rounded-full transition-colors ${
                      selectedTags.includes(tag)
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Available Crew Members */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Available Crew Members ({filteredCrewMembers.length})
            </h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredCrewMembers
                .filter((member) => !selectedCrew.includes(member.id))
                .map((member) => (
                  <div
                    key={member.id}
                    className="bg-gray-50 rounded-lg p-3 flex justify-between items-center hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-sm">
                        {member.profile_image ? (
                          <img
                            src={member.profile_image}
                            alt={`${member.first_name} ${member.last_name}`}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          `${member.first_name.charAt(
                            0
                          )}${member.last_name.charAt(0)}`
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {member.role_type}
                        </p>
                        {member.specialties &&
                          member.specialties.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {member.specialties
                                .slice(0, 3)
                                .map((specialty) => (
                                  <span
                                    key={specialty}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {specialty}
                                  </span>
                                ))}
                            </div>
                          )}
                      </div>
                    </div>
                    <button
                      onClick={() => addCrewMember(member.id)}
                      className="text-sm text-primary hover:text-primary/80"
                    >
                      Add
                    </button>
                  </div>
                ))}
              {filteredCrewMembers.filter(
                (member) => !selectedCrew.includes(member.id)
              ).length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No available crew members found.
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
